import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContentEditor from './components/ContentEditor';
import AdminHeader from './components/AdminHeader';
import {
  fetchUrlContent,
  saveContent,
  updateContent,
  getAllSavedContent,
  getContentById,
  deleteContent,
  toggleHideContent,
} from './services/api';
import { processHtmlForPreview } from './services/htmlProcessor';

function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [preview, setPreview] = useState(null);
  const [savedContent, setSavedContent] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [extractedImages, setExtractedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState('');
  const [enableRewriting, setEnableRewriting] = useState(true);
  const [isPreviewCollapsed, setIsPreviewCollapsed] = useState(false);
  const [progressStep, setProgressStep] = useState('');
  const [rewriteOptions, setRewriteOptions] = useState({
    imageCount: 1,
    includeSummary: true,
    contentStyle: 'professional',
    imageStyle: 'realistic',
    useOriginalImages: false,
    imageSource: 'dalle' // dalle, gemini, unsplash, pexels
  });
  const [showOptions, setShowOptions] = useState(true);
  const [editedContent, setEditedContent] = useState(null);
  const [editingId, setEditingId] = useState(null); // Track which article is being edited

  // Load saved content on mount
  useEffect(() => {
    loadSavedContent();
  }, []);

  const loadSavedContent = async (page = 1) => {
    try {
      setLoadingSaved(true);
      const response = await getAllSavedContent(page, 20, false);
      if (response.success) {
        setSavedContent(response.data);
        setCurrentPage(response.pagination.page);
        setTotalPages(response.pagination.totalPages);
        setTotalItems(response.pagination.total);
      }
    } catch (error) {
      console.error('Failed to load saved content:', error);
    } finally {
      setLoadingSaved(false);
    }
  };

  const handleFetch = async () => {
    if (!url.trim()) {
      setStatus({ type: 'error', message: 'Please enter a URL' });
      return;
    }

    // Add protocol if missing
    let fetchUrl = url.trim();
    if (!fetchUrl.startsWith('http://') && !fetchUrl.startsWith('https://')) {
      fetchUrl = 'https://' + fetchUrl;
      setUrl(fetchUrl);
    }

    setLoading(true);
    setRewriting(false);
    setPreview(null);
    setIsPreviewCollapsed(false);

    console.log('Fetching with options:', enableRewriting ? rewriteOptions : false);

    // Show progressive status messages
    setProgressStep('fetch');
    setStatus({ type: 'loading', message: '🌐 Fetching content from URL...' });

    // Simulate progress updates (since we don't have real-time updates yet)
    const progressTimer = setTimeout(() => {
      if (enableRewriting) {
        setProgressStep('analyze');
        setStatus({ type: 'loading', message: '🔍 Analyzing content and detecting language...' });
        setTimeout(() => {
          setProgressStep('rewrite');
          setStatus({ type: 'loading', message: '✍️ Rewriting content in English...' });
          setTimeout(() => {
            setProgressStep('images');
            setStatus({ type: 'loading', message: '🎨 Generating AI images...' });
          }, 3000);
        }, 2000);
      }
    }, 1500);

    try {
      const response = await fetchUrlContent(fetchUrl, enableRewriting ? rewriteOptions : false);
      clearTimeout(progressTimer);
      
      if (response.success) {
        const data = response.data;
        
        // Check if content was rewritten
        if (data.isRewritten) {
          setRewriting(true);
          setStatus({ 
            type: 'info', 
            message: `✨ Content detected in ${data.originalLanguage}, intelligently rewritten in English. Please review before saving.`
          });
        } else if (data.languageInfo && !data.languageInfo.isEnglish) {
          setStatus({ 
            type: 'warning', 
            message: `Content is in ${data.languageInfo.language}. Automatic rewriting was attempted but may have failed.`
          });
        } else {
          setStatus({ 
            type: 'success', 
            message: `Successfully fetched: ${data.title}`
          });
        }
        
        setPreview(data);
        
        // Extract images from HTML
        const images = extractImagesFromHtml(data.html);
        setExtractedImages(images);
        
        // Use AI-selected image if available, otherwise first extracted image
        setSelectedImage(data.imageUrl || (images.length > 0 ? images[0] : ''));
        
      } else {
        setStatus({ type: 'error', message: response.message || 'Failed to fetch content' });
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch URL';
      setStatus({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  const extractImagesFromHtml = (html) => {
    const images = [];
    
    // Extract og:image meta tag using regex
    const ogImageMatch = html.match(/<meta[^>]*property=["']og:image["'][^>]*content=["']([^"']+)["']/i);
    if (ogImageMatch && ogImageMatch[1]) {
      const ogUrl = ogImageMatch[1];
      // Handle protocol-relative URLs
      const fullUrl = ogUrl.startsWith('//') ? 'https:' + ogUrl : ogUrl;
      images.push(fullUrl);
    }
    
    // Extract all img src attributes using regex
    const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
    let match;
    while ((match = imgRegex.exec(html)) !== null) {
      let src = match[1];
      // Handle protocol-relative URLs
      if (src.startsWith('//')) {
        src = 'https:' + src;
      }
      // Include http/https URLs, data URLs, and protocol-relative URLs
      if ((src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:image')) && !images.includes(src)) {
        images.push(src);
      }
    }
    
    console.log('Extracted images:', images);
    return images;
  };

  const handleSave = async () => {
    if (!preview) return;

    setSaving(true);
    setStatus({ type: 'loading', message: editingId ? 'Updating content...' : 'Saving to database...' });

    try {
      // Use edited content if available, otherwise use original preview content
      const contentToSave = editedContent || preview.html;
      
      let response;
      
      if (editingId) {
        // Update existing content
        response = await updateContent(
          editingId,
          preview.title,
          contentToSave,
          selectedImage
        );
      } else {
        // Create new content - Include all rewritten content metadata
        response = await saveContent(
          preview.url, 
          preview.title, 
          contentToSave, 
          selectedImage,
          {
            summary: preview.summary,
            topic: preview.topic,
            categories: preview.categories,
            keywords: preview.keywords,
            isRewritten: preview.isRewritten,
            originalTitle: preview.originalTitle,
            originalLanguage: preview.originalLanguage
          }
        );
      }
      
      if (response.success) {
        setStatus({ type: 'success', message: editingId ? '✅ Content updated successfully!' : '✅ Content saved successfully!' });
        setPreview(null);
        setUrl('');
        setExtractedImages([]);
        setSelectedImage('');
        setRewriting(false);
        setEditedContent(null);
        setEditingId(null);
        loadSavedContent(); // Refresh the list
      } else {
        setStatus({ type: 'error', message: response.message || 'Failed to save content' });
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to save content';
      setStatus({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPreview(null);
    setExtractedImages([]);
    setSelectedImage('');
    setRewriting(false);
    setEditedContent(null);
    setEditingId(null);
    setStatus({ type: '', message: '' });
  };

  const handleEdit = async (id) => {
    try {
      setLoading(true);
      setStatus({ type: 'loading', message: 'Loading article for editing...' });
      
      const response = await getContentById(id);
      
      if (response.success) {
        const article = response.data;
        
        // Set up the preview with the existing article data
        setPreview({
          url: article.url,
          title: article.title,
          html: article.htmlContent,
        });
        
        // Set the editing ID
        setEditingId(id);
        
        // Extract and set image
        const images = extractImagesFromHtml(article.htmlContent);
        setExtractedImages(images);
        setSelectedImage(article.imageUrl || (images.length > 0 ? images[0] : ''));
        
        // Scroll to top so user sees the editor
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        setStatus({ type: 'info', message: '✏️ Editing mode - Make changes and click Save to update' });
      } else {
        setStatus({ type: 'error', message: 'Failed to load article' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to load article for editing' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) {
      return;
    }

    try {
      const response = await deleteContent(id);
      if (response.success) {
        loadSavedContent(currentPage);
        setStatus({ type: 'success', message: 'Content deleted successfully' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to delete content' });
    }
  };

  const handleHide = async (id) => {
    try {
      const response = await toggleHideContent(id);
      if (response.success) {
        loadSavedContent(currentPage);
        setStatus({ type: 'success', message: response.message });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to toggle hide status' });
    }
  };

  const handleView = async (id) => {
    try {
      const response = await getContentById(id);
      if (response.success) {
        setPreview({
          url: response.data.url,
          title: response.data.title,
          html: response.data.htmlContent,
          contentLength: response.data.contentLength,
          fetchedAt: response.data.fetchedAt,
          isSaved: true,
        });
        setShowPreviewModal(true);
        setStatus({ type: '', message: '' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to load content' });
    }
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
  };

  const handleOpenInNewTab = () => {
    if (preview && preview.html) {
      const newWindow = window.open();
      newWindow.document.write(preview.html);
      newWindow.document.close();
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadSavedContent(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="app-wrapper">
      <AdminHeader
        title="Dashboard"
        subtitle="Fetch, preview, and save web content to your database"
      />
    <div className="app">

      {/* URL Input Section */}
      <section className="url-input-section">
        <div className="input-group">
          <input
            type="text"
            className="url-input"
            placeholder="Enter URL (e.g., https://example.com)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !loading && handleFetch()}
            disabled={loading}
          />
          <button
            className="btn btn-primary"
            onClick={handleFetch}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Fetching...
              </>
            ) : (
              '🔍 Fetch Content'
            )}
          </button>
        </div>

        <div style={{ marginTop: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9em', color: '#555' }}>
              <input
                type="checkbox"
                checked={enableRewriting}
                onChange={(e) => setEnableRewriting(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span>✨ Enable AI Rewriting for non-English content</span>
            </label>
            {enableRewriting && (
              <button
                onClick={() => setShowOptions(!showOptions)}
                style={{
                  background: 'none',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  color: '#555'
                }}
              >
                ⚙️ {showOptions ? 'Hide' : 'Show'} Options
              </button>
            )}
            {!enableRewriting && (
              <span style={{ fontSize: '0.85em', color: '#f39c12', fontStyle: 'italic' }}>
                (Rewriting disabled - will show original content)
              </span>
            )}
          </div>

          {/* Rewriting Options Panel */}
          {enableRewriting && showOptions && (
            <div style={{
              background: '#f8f9fa',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '15px',
              marginTop: '10px'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95em', color: '#333' }}>
                ⚙️ Rewriting Options
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                {/* Image Count */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '5px' }}>
                    📷 Number of Images
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={rewriteOptions.imageCount}
                    onChange={(e) => setRewriteOptions({...rewriteOptions, imageCount: parseInt(e.target.value)})}
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: '0.8em', color: '#777', marginTop: '3px' }}>
                    {rewriteOptions.imageCount} {rewriteOptions.imageCount === 1 ? 'image' : 'images'}
                  </div>
                </div>

                {/* Use Original Images */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85em', color: '#555' }}>
                    <input
                      type="checkbox"
                      checked={rewriteOptions.useOriginalImages}
                      onChange={(e) => setRewriteOptions({...rewriteOptions, useOriginalImages: e.target.checked})}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>🖼️ Use Original Article Images</span>
                  </label>
                  <div style={{ fontSize: '0.75em', color: '#777', marginTop: '5px', marginLeft: '24px' }}>
                    {rewriteOptions.useOriginalImages ? 'Keep images from source' : 'Generate new topic-relevant images'}
                  </div>
                </div>

                {/* Include Summary */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85em', color: '#555' }}>
                    <input
                      type="checkbox"
                      checked={rewriteOptions.includeSummary}
                      onChange={(e) => setRewriteOptions({...rewriteOptions, includeSummary: e.target.checked})}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>💡 Include Summary</span>
                  </label>
                  <div style={{ fontSize: '0.75em', color: '#777', marginTop: '5px', marginLeft: '24px' }}>
                    Add a brief summary at the top
                  </div>
                </div>

                {/* Content Style */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '5px' }}>
                    ✍️ Writing Style
                  </label>
                  <select
                    value={rewriteOptions.contentStyle}
                    onChange={(e) => setRewriteOptions({...rewriteOptions, contentStyle: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '6px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '0.85em'
                    }}
                  >
                    <option value="professional">Professional</option>
                    <option value="casual">Casual & Friendly</option>
                    <option value="technical">Technical & Detailed</option>
                    <option value="simple">Simple & Easy to Read</option>
                  </select>
                </div>

                {/* Image Style */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '5px' }}>
                    🎨 Image Style
                  </label>
                  <select
                    value={rewriteOptions.imageStyle}
                    onChange={(e) => setRewriteOptions({...rewriteOptions, imageStyle: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '6px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '0.85em'
                    }}
                  >
                    <option value="realistic">Realistic Photos</option>
                    <option value="illustration">Illustrations</option>
                    <option value="minimal">Minimal & Clean</option>
                    <option value="vibrant">Vibrant & Colorful</option>
                  </select>
                </div>

                {/* Image Source (OpenAI only) */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '5px' }}>
                    🌐 Image Source
                  </label>
                  <select
                    value={rewriteOptions.imageSource}
                    onChange={(e) => setRewriteOptions({...rewriteOptions, imageSource: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '6px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '0.85em'
                    }}
                  >
                    <option value="dalle">🎨 DALL-E (AI Generated)</option>
                    <option value="gemini">✨ Gemini (Google AI)</option>
                  </select>
                  <div style={{ fontSize: '0.75em', color: '#777', marginTop: '5px' }}>
                    AI-generated unique images (OpenAI DALL-E or Google Gemini)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {status.message && (
          <div className={`status ${status.type}`}>
            {status.message}
          </div>
        )}
      </section>

      {/* Content Preview Section - Only show when not in modal mode */}
      {preview && !preview.isSaved && !showPreviewModal && (
        <section className="content-preview-section">
          <div className="preview-header">
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2 style={{ margin: 0 }}>{preview.title}</h2>
                <button
                  onClick={() => setIsPreviewCollapsed(!isPreviewCollapsed)}
                  style={{
                    background: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    color: '#555',
                    marginLeft: '15px'
                  }}
                >
                  {isPreviewCollapsed ? '▼ Show Content' : '▲ Hide Content'}
                </button>
              </div>
              {editingId && (
                <div className="rewritten-badge" style={{
                  background: '#fff3e0',
                  border: '2px solid #ff9800',
                  padding: '10px 15px',
                  borderRadius: '6px',
                  marginTop: '10px',
                  marginBottom: '10px'
                }}>
                  <strong>✏️ Edit Mode</strong>
                  <div style={{ fontSize: '0.9em', marginTop: '5px', color: '#555' }}>
                    You are editing an existing article. Make your changes and click Update to save.
                  </div>
                </div>
              )}
              {preview.isRewritten && (
                <div className="rewritten-badge" style={{
                  background: '#e3f2fd',
                  border: '1px solid #2196f3',
                  padding: '10px 15px',
                  borderRadius: '6px',
                  marginTop: '10px',
                  marginBottom: '10px'
                }}>
                  <strong>✨ Intelligently Rewritten Content</strong>
                  <div style={{ fontSize: '0.9em', marginTop: '5px', color: '#555' }}>
                    {preview.originalTitle && (
                      <div>📝 Original: {preview.originalTitle}</div>
                    )}
                    {preview.originalLanguage && (
                      <div>🌍 Language: {preview.originalLanguage} → English</div>
                    )}
                    {preview.topic && (
                      <div>🏷️ Topic: {preview.topic}</div>
                    )}
                    {preview.summary && (
                      <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#666' }}>
                        💡 {preview.summary}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="preview-meta">
                <span>📄 {formatSize(preview.contentLength)}</span>
                <span>🕐 {formatDate(preview.fetchedAt)}</span>
                {preview.categories && preview.categories.length > 0 && (
                  <span>🏷️ {preview.categories.join(', ')}</span>
                )}
              </div>
            </div>
            <div className="preview-actions">
              {!preview.isSaved && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner"></span>
                        Saving...
                      </>
                    ) : (
                      editingId ? '💾 Update Content' : '💾 Save to Database'
                    )}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleDiscard}
                    disabled={saving}
                  >
                    ✖ Discard
                  </button>
                </>
              )}
            </div>
          </div>
          
          {!isPreviewCollapsed && (
            <ContentEditor
              initialContent={preview.html}
              onContentChange={(content) => setEditedContent(content)}
              title={preview.title}
            />
          )}
        </section>
      )}

      {/* Saved Content Section */}
      <section className="saved-content-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>💾 Saved Content</h2>
          {totalItems > 0 && (
            <div style={{ fontSize: '0.9em', color: '#666' }}>
              Showing {((currentPage - 1) * 20) + 1} - {Math.min(currentPage * 20, totalItems)} of {totalItems} items
            </div>
          )}
        </div>
        
        {loadingSaved ? (
          <div className="empty-state">
            <p>Loading saved content...</p>
          </div>
        ) : savedContent.length === 0 ? (
          <div className="empty-state">
            <p>No saved content yet. Fetch a URL and save it!</p>
          </div>
        ) : (
          <>
            <ul className="saved-list">
              {savedContent.map((item) => (
                <li key={item.id} className="saved-item" style={{ opacity: item.isHidden ? 0.6 : 1 }}>
                  <div className="saved-item-info">
                    <div className="saved-item-title">
                      {item.title}
                      {item.isHidden && (
                        <span style={{ 
                          marginLeft: '10px', 
                          padding: '2px 8px', 
                          background: '#f0f0f0', 
                          borderRadius: '4px', 
                          fontSize: '0.8em',
                          color: '#666'
                        }}>
                          🔒 Hidden
                        </span>
                      )}
                    </div>
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="saved-item-url"
                    >
                      {item.url}
                    </a>
                    <div className="saved-item-meta">
                      {formatSize(item.contentLength)} • Saved {formatDate(item.createdAt)}
                    </div>
                  </div>
                  <div className="saved-item-actions">
                    <button
                      className="btn btn-primary btn-small"
                      onClick={() => handleView(item.id)}
                    >
                      👁 View
                    </button>
                    <button
                      className="btn btn-success btn-small"
                      onClick={() => handleEdit(item.id)}
                      title="Edit this article"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={() => handleHide(item.id)}
                    >
                      {item.isHidden ? '👁 Unhide' : '🔒 Hide'}
                    </button>
                    <button
                      className="btn btn-danger btn-small"
                      onClick={() => handleDelete(item.id)}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                marginTop: '30px',
                padding: '20px'
              }}>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  ⏮ First
                </button>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ◀ Previous
                </button>
                <span style={{ padding: '0 15px', fontSize: '0.95em', color: '#666' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next ▶
                </button>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last ⏭
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Preview Modal */}
      {showPreviewModal && preview && (
        <div className="modal-overlay" onClick={handleClosePreviewModal}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{preview.title}</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-secondary btn-small" onClick={handleOpenInNewTab}>
                  🔗 Open in New Tab
                </button>
                <button className="modal-close" onClick={handleClosePreviewModal}>
                  ✖ Close
                </button>
              </div>
            </div>
            <div className="modal-content">
              <iframe
                className="content-frame"
                srcDoc={processHtmlForPreview(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <style>
                      body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        font-size: 16px;
                        line-height: 1.6;
                        color: #333;
                        padding: 20px;
                        margin: 0;
                      }
                      img { max-width: 100%; height: auto; margin: 1.5em 0; }
                    </style>
                  </head>
                  <body>${preview.html}</body>
                  </html>
                `)}
                title="Content Preview"
                sandbox="allow-same-origin allow-popups"
              />
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default App;
